import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import SimulatorContext from "../../../../context/SimulatorContext";

interface Props {
  tubingScreen?: boolean;
  pumpGuard?: boolean;
  superPerf?: boolean;
}

const spOptions = ['2-3/8"', '2-7/8"', '3-1/2"'];

const tsOptions = [
  "2-3/8\" x 8'",
  "2-7/8\" x 8'",
  "3-1/2\" x 8'",
  "2-3/8\" x 23.5'",
  "2-7/8\" x 23.5'",
  "3-1/2\" x 23.5'",
];

const pgOptions = [
  '1" x 9"',
  "1\" x 2'",
  "1\" x 3'",
  "1\" x 6'",
  "1\" x 8'",
  "1\" x 10'",
  '1-1/4" x 9"',
  "1-1/4\" x 2'",
  "1-1/4\" x 3'",
  "1-1/4\" x 6'",
  "1-1/4\" x 8'",
  "1-1/4\" x 10'",
  '1-1/2" x 9"',
  "1-1/2\" x 2'",
  "1-1/2\" x 3'",
  "1-1/2\" x 6'",
  "1-1/2\" x 8'",
  "1-1/2\" x 10'",
];

const slotOptions = [
  "0.006",
  "0.008",
  "0.01",
  "0.012",
  "0.015",
  "0.018",
  "0.02",
  "0.025",
  "0.03",
  "0.035",
  "0.05",
  "0.075",
  "0.125",
];

const slotPgOptions = [
  "0.006",
  "0.012",
  "0.018",
  "0.025",
  "0.035",
  "0.05",
  "0.075",
  "0.125",
];

const slotSpOptions = ["0.075"];

const wellClasification = ["AVERAGE", "BAD", "SEVERE"];

export const SandSimulatorForm = ({
  tubingScreen,
  pumpGuard,
  superPerf,
}: Props) => {
  const { sandSimulatorValues, onUpdateSandSimulatorValues, onSimulateSand } =
    useContext(SimulatorContext);

  const handleUpdateValues = (item: string, value: any) => {
    if (parseFloat(value) >= 0) onUpdateSandSimulatorValues(item, value);
  };
  return (
    <Box
      sx={{
        pt: 1,
        "& .MuiTextField-root": { width: "100%" },
        "& .MuiAutocomplete-root": { width: "100%" },
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          backgroundColor: "rgba(255,255,255,0.7)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
          "&.Mui-focused": { backgroundColor: "#fff" },
        },
      }}
    >
      <FormGroup>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{
                mt: 1,
                mb: 0.5,
                fontWeight: 700,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                color: "rgb(140, 150, 169)",
              }}
            >
              General Data Entry
            </Typography>
            <Divider sx={{ borderColor: "rgba(140,150,169,0.2)", mb: 2 }} />
          </Grid>
          <Grid item lg={4} sm={6} xs={12}>
            <TextField
              type="number"
              label="Production of Total liquid barrel (BFPD)"
              value={sandSimulatorValues.bfpd}
              variant="outlined"
              onChange={(event) =>
                handleUpdateValues("bfpd", event.target.value)
              }
            />
          </Grid>
          <Grid item lg={4} sm={6} xs={12}>
            <TextField
              type="number"
              label="Percentage of Runtime"
              value={sandSimulatorValues.percentageRuntime}
              variant="outlined"
              onChange={(event) =>
                handleUpdateValues("percentageRuntime", event.target.value)
              }
            />
          </Grid>
          {tubingScreen && (
            <Grid item lg={4} sm={6} xs={12}>
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                value={sandSimulatorValues.selectedTubingScreen}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("selectedTubingScreen", newValue)
                }
                {...{
                  options: tsOptions,
                }}
                disablePortal
                  renderInput={(params) => (
                  <TextField {...params} label="Selected Tubing Screen" />
                )}
              />
            </Grid>
          )}
          {superPerf && (
            <Grid item lg={4} sm={6} xs={12}>
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                value={sandSimulatorValues.selectedSuperPerf}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("selectedSuperPerf", newValue)
                }
                {...{
                  options: spOptions,
                }}
                disablePortal
                  renderInput={(params) => (
                  <TextField {...params} label="Selected Super Perf" />
                )}
              />
            </Grid>
          )}
          {pumpGuard && (
            <Grid item lg={4} sm={6} xs={12}>
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                value={sandSimulatorValues.selectedPumbGuard}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("selectedPumbGuard", newValue)
                }
                {...{
                  options: pgOptions,
                }}
                disablePortal
                  renderInput={(params) => (
                  <TextField {...params} label="Selected Pump Guard" />
                )}
              />
            </Grid>
          )}
          <Grid item lg={4} sm={6} xs={12}>
            {pumpGuard && (
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                value={sandSimulatorValues.slotPg}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("slotPg", newValue)
                }
                {...{
                  options: slotPgOptions,
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                disablePortal
                  renderInput={(params) => <TextField {...params} label="Slot" />}
              />
            )}
            {tubingScreen && (
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                value={sandSimulatorValues.slot}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("slot", newValue)
                }
                {...{
                  options: slotOptions,
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                disablePortal
                  renderInput={(params) => <TextField {...params} label="Slot" />}
              />
            )}
            {superPerf && (
              <Autocomplete
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }}
                value={sandSimulatorValues.slotSp}
                onChange={(_, newValue) =>
                  onUpdateSandSimulatorValues("slotSp", newValue)
                }
                {...{
                  options: slotSpOptions,
                }}
                isOptionEqualToValue={(option, value) => {
                  return true;
                }}
                disablePortal
                  renderInput={(params) => <TextField {...params} label="Slot" />}
              />
            )}
          </Grid>
          <Grid item lg={4} sm={6} xs={12}>
            <Autocomplete
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option}>
                    {option}
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => {
                return true;
              }}
              value={sandSimulatorValues.wellClasification}
              onChange={(_, newValue) =>
                onUpdateSandSimulatorValues("wellClasification", newValue)
              }
              {...{
                options: wellClasification,
              }}
              disablePortal
              renderInput={(params) => (
                <TextField {...params} label="Well Clasification" />
              )}
            />
          </Grid>

          <Grid item lg={4} sm={6} xs={12}>
            <TextField
              type="number"
              label="Open Area of Screen in2"
              value={sandSimulatorValues.openAreaOfScreen}
              variant="outlined"
              disabled
            />
          </Grid>
          {tubingScreen && (
            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="# Tubing Screen"
                value={sandSimulatorValues.numberOfTubingScreen}
                variant="outlined"
                  onChange={(event) =>
                  handleUpdateValues("numberOfTubingScreen", event.target.value)
                }
              />
            </Grid>
          )}
          {superPerf && (
            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="# Tubing Screen"
                value={sandSimulatorValues.numberOfTubingScreen}
                variant="outlined"
                  onChange={(event) =>
                  handleUpdateValues("numberOfTubingScreen", event.target.value)
                }
              />
            </Grid>
          )}
          {pumpGuard && (
            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="# Pump Guards"
                value={sandSimulatorValues.numberOfPumpGuard}
                variant="outlined"
                  onChange={(event) =>
                  handleUpdateValues("numberOfPumpGuard", event.target.value)
                }
              />
            </Grid>
          )}
        </Grid>
      </FormGroup>
      <Button
        variant="contained"
        disableElevation
        sx={{
          mt: 4,
          borderRadius: "12px",
          px: 4,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          backgroundColor: "rgb(140, 150, 169)",
          "&:hover": { backgroundColor: "rgb(115, 125, 145)" },
        }}
        onClick={onSimulateSand}
      >
        Simulate
      </Button>
    </Box>
  );
};
